/**
 * Catmull-Rom spline interpolation for track centerline generation.
 * Takes an array of control points and produces a smooth curve passing
 * through all of them.
 */

export interface Point2D {
  x: number;
  y: number;
}

/**
 * Evaluate a Catmull-Rom spline at parameter t (0..1) between points p1 and p2,
 * using p0 and p3 as neighboring control points for tangent computation.
 */
function catmullRomPoint(
  p0: Point2D,
  p1: Point2D,
  p2: Point2D,
  p3: Point2D,
  t: number
): Point2D {
  const t2 = t * t;
  const t3 = t2 * t;

  return {
    x:
      0.5 *
      (2 * p1.x +
        (-p0.x + p2.x) * t +
        (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
        (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
    y:
      0.5 *
      (2 * p1.y +
        (-p0.y + p2.y) * t +
        (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
        (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
  };
}

/**
 * Generate a smooth closed curve through the given control points using
 * Catmull-Rom interpolation. Returns evenly-ish spaced points along the curve.
 *
 * @param controlPoints - Array of 2D points the curve passes through (closed loop)
 * @param pointsPerSegment - Number of interpolated points between each pair of control points
 */
export function interpolateClosedSpline(
  controlPoints: Point2D[],
  pointsPerSegment: number = 20
): Point2D[] {
  const n = controlPoints.length;
  const result: Point2D[] = [];

  for (let i = 0; i < n; i++) {
    const p0 = controlPoints[(i - 1 + n) % n];
    const p1 = controlPoints[i];
    const p2 = controlPoints[(i + 1) % n];
    const p3 = controlPoints[(i + 2) % n];

    for (let j = 0; j < pointsPerSegment; j++) {
      const t = j / pointsPerSegment;
      result.push(catmullRomPoint(p0, p1, p2, p3, t));
    }
  }

  return result;
}
